import React, { useState, useEffect } from 'react';
import DashboardSidebar from '../../components/admin/organisms/DashboardSidebar';
import DashboardHeader from '../../components/admin/organisms/DashboardHeader';
import DashboardOverview from '../../components/admin/organisms/DashboardOverview';
import ProductsPage from './ProductsPage';
import SettingsPage from './SettingsPage';
import AvailabilityPage from './AvailabilityPage';
import OrdersPage from './OrdersPage';
import StorefrontPage from './StorefrontPage';
import CouponsPage from './CouponsPage';
import { NavItemData, createDateFromString } from '../../types';
import { orderService } from '../../services/orderService';
import './AdminDashboard.css';

interface DashboardStats {
  totalOrders: number;
  processingOrders: number;
  completedOrders: number;
  totalRevenue: number;
}

interface Activity {
  type: 'order' | 'product' | 'customer';
  title: string;
  description: string;
  time: string;
}

interface AdminDashboardProps {
  onLogout: () => void;
  stats?: DashboardStats;
}

interface DashboardData {
  stats: DashboardStats;
  activities: Activity[];
  loading: boolean;
  error: string | null;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ onLogout, stats }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const [dashboardData, setDashboardData] = useState<DashboardData>({
    stats: {
      totalOrders: 0,
      processingOrders: 0,
      completedOrders: 0,
      totalRevenue: 0,
    },
    activities: [],
    loading: true,
    error: null
  });

  const currentStats = stats || dashboardData.stats;

  const formatDateInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Date range filter (controls overview stats + recent activity)
  const [dateRangeFrom, setDateRangeFrom] = useState<string>(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return formatDateInput(startOfMonth);
  });
  const [dateRangeTo, setDateRangeTo] = useState<string>(() => formatDateInput(new Date()));

  // Fetch dashboard data function
  const fetchDashboardData = async () => {
    try {
      setDashboardData(prev => ({ ...prev, loading: true, error: null }));

      const startDate = createDateFromString(dateRangeFrom);
      const endDate = createDateFromString(dateRangeTo);
      // Include the full end day in our createdAt range queries
      endDate.setHours(23, 59, 59, 999);
      const dateRange = { start: startDate, end: endDate };

      // Fetch order statistics
      const orderStats = await orderService.getOrderStats(dateRange);
      
      // Fetch recent orders for activities
      const recentOrders = await orderService.getOrders({ dateRange }, 10, 0);

      // Convert order stats to dashboard stats format
      const dashboardStats: DashboardStats = {
        totalOrders: orderStats.totalOrders,
        processingOrders: orderStats.ordersByStatus.processing || 0,
        completedOrders: orderStats.completedOrders,
        totalRevenue: Math.round(orderStats.totalRevenue)
      };

      // Convert recent orders to activities
      const activities: Activity[] = recentOrders.map(order => {
        const timeAgo = getTimeAgo(order.createdAt);
        return {
          type: 'order' as const,
          title: `Order ${order.orderNumber}`,
          description: `${order.customerName} - ${order.status} - €${order.total.toFixed(2)}`,
          time: timeAgo
        };
      });

      setDashboardData({
        stats: dashboardStats,
        activities,
        loading: false,
        error: null
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load dashboard data'
      }));
    }
  };

  // Check for mobile viewport and handle sidebar state
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 1024;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch dashboard data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Helper function to calculate time ago
  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  const navItems: NavItemData[] = [
    { id: 'overview', label: 'Overview', icon: 'dashboard' },
    { id: 'orders', label: 'Orders', icon: 'cart' },
    { id: 'products', label: 'Products', icon: 'cake' },
    { id: 'storefront', label: 'Storefront', icon: 'storefront' },
    { id: 'availability', label: 'Availability', icon: 'calendar' },
    { id: 'coupons', label: 'Coupons', icon: 'chart' },
    { id: 'customers', label: 'Customers', icon: 'users' },
    { id: 'analytics', label: 'Analytics', icon: 'chart' },
    { id: 'settings', label: 'Settings', icon: 'gear' }
  ];

  // Use real activities from dashboard data
  const activities = dashboardData.activities;

  const renderContent = () => {
    // Show loading state for overview tab
    if (activeTab === 'overview' && dashboardData.loading) {
      return (
        <div className="tab-content">
          <div className="loading-state">
            <h2>Loading Dashboard...</h2>
            <p>Please wait while we fetch your data.</p>
          </div>
        </div>
      );
    }

    // Show error state for overview tab
    if (activeTab === 'overview' && dashboardData.error) {
      return (
        <div className="tab-content">
          <div className="error-state">
            <h2>Error Loading Dashboard</h2>
            <p>{dashboardData.error}</p>
            <button 
              onClick={fetchDashboardData} 
              className="retry-button"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'overview':
        return (
          <>
            {/* Date Range Filter Card */}
            <div className="dashboard-filter-card">
              <div className="filter-card-header">
                <div>
                  <h3>Date Range Filter</h3>
                  <p>Select period to view dashboard metrics</p>
                </div>
              </div>
              <div className="filter-card-content">
                <div className="dashboard-overview-filter">
                  <label htmlFor="overview-date-from">From Date</label>
                  <input
                    id="overview-date-from"
                    type="date"
                    value={dateRangeFrom}
                    onChange={(e) => setDateRangeFrom(e.target.value)}
                  />
                </div>
                <div className="dashboard-overview-filter">
                  <label htmlFor="overview-date-to">To Date</label>
                  <input
                    id="overview-date-to"
                    type="date"
                    value={dateRangeTo}
                    onChange={(e) => setDateRangeTo(e.target.value)}
                  />
                </div>
                <div className="dashboard-overview-filter-actions">
                  <button
                    className="overview-apply-btn"
                    onClick={fetchDashboardData}
                    disabled={dashboardData.loading}
                  >
                    {dashboardData.loading ? 'Applying…' : 'Apply Filter'}
                  </button>
                </div>
              </div>
            </div>
            <DashboardOverview stats={currentStats} activities={activities} />
          </>
        );
      case 'orders':
        return <OrdersPage />;
      case 'products':
        return <ProductsPage />;
      case 'storefront':
        return <StorefrontPage />;
      case 'availability':
        return <AvailabilityPage />;
      case 'coupons':
        return <CouponsPage />;
      case 'customers':
        return <div className="tab-content"><h2>Customer Management</h2><p>Customer management interface coming soon...</p></div>;
      case 'analytics':
        return <div className="tab-content"><h2>Analytics</h2><p>Analytics dashboard coming soon...</p></div>;
      case 'settings':
        return <SettingsPage />;
      default:
        return <DashboardOverview stats={currentStats} activities={activities} />;
    }
  };

  const currentTab = navItems.find(item => item.id === activeTab);
  const pageTitle = currentTab?.label || 'Overview';

  // Handle sidebar toggle
  const handleSidebarToggle = () => {
    if (isMobile) {
      setSidebarOpen(!sidebarOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  // Handle tab change and close mobile sidebar
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Close sidebar when clicking outside on mobile
  const handleOverlayClick = () => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="admin-dashboard">
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div className="mobile-overlay" onClick={handleOverlayClick} />
      )}

      <DashboardSidebar
        navItems={navItems}
        activeTab={activeTab}
        isCollapsed={isMobile ? false : sidebarCollapsed}
        isOpen={isMobile ? sidebarOpen : undefined}
        onTabChange={handleTabChange}
        onLogout={onLogout}
      />

      <main className="dashboard-main">
        <DashboardHeader
          title={pageTitle}
          onToggleSidebar={handleSidebarToggle}
          onRefresh={activeTab === 'overview' ? fetchDashboardData : undefined}
          isRefreshing={dashboardData.loading}
        />
        

        <div className="dashboard-content">
          {renderContent()}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
